import { createSlice } from '@reduxjs/toolkit';

import { DataState } from '../../../constants';
import { PodcastEpisodeModel, PodcastModel } from '../../../models/Podcast.model';

export interface PodcastSliceState {
  [tabId: number]: {
    data: {
      podcastList: Partial<PodcastModel>[];
      latestEpisodes: PodcastEpisodeModel[];
    } | null;
    state: DataState;
  };
  podcastFeeds: {
    [podcastUrl: string]: {
      data: PodcastModel | null;
      state: DataState;
      currentPage: number;
      lastPage: number;
      order: 'desc' | 'asc';
    };
  };
}

const initialState: PodcastSliceState = {
  podcastFeeds: {},
};

const podcastSlice = createSlice({
  name: 'podcast',
  initialState,
  reducers: {
    setPodcastListAndEpisodesLoading(state, action) {
      const tabId = action.payload.tabId;
      if (!state[tabId]) {
        state[tabId] = { data: null, state: DataState.NOT_LOADED };
      }
      state[tabId].state = DataState.LOADING;
    },
    setPodcastListAndEpisodesLoaded(state, action) {
      const { tabId, podcasts, lastEpisodes } = action.payload;
      if (!state[tabId]) {
        state[tabId] = { data: null, state: DataState.NOT_LOADED };
      }
      state[tabId].data = {
        podcastList: podcasts,
        latestEpisodes: lastEpisodes,
      };
      state[tabId].state = DataState.LOADED;
    },
    setPodcastListAndEpisodesError(state, action) {
      const tabId = action.payload.tabId;
      if (!state[tabId]) {
        state[tabId] = { data: null, state: DataState.NOT_LOADED };
      }
      state[tabId].state = DataState.ERROR;
    },
    setPodcastFeedDataLoading(state, action) {
      const podcastUrl = action.payload.podcastUrl;
      if (!state.podcastFeeds[podcastUrl]) {
        state.podcastFeeds[podcastUrl] = {
          data: null,
          state: DataState.NOT_LOADED,
          currentPage: 1,
          lastPage: 1,
          order: 'desc',
        };
      }
      state.podcastFeeds[podcastUrl].state = DataState.LOADING;
    },
    setPodcastFeedDataLoaded(state, action) {
      const { podcastUrl, podcastData } = action.payload;
      if (!state.podcastFeeds[podcastUrl]) {
        state.podcastFeeds[podcastUrl] = {
          data: null,
          state: DataState.NOT_LOADED,
          currentPage: 1,
          lastPage: 1,
          order: 'desc',
        };
      }
      state.podcastFeeds[podcastUrl].data = podcastData;
      state.podcastFeeds[podcastUrl].state = DataState.LOADED;
      state.podcastFeeds[podcastUrl].currentPage = podcastData.current_page || 1;
      state.podcastFeeds[podcastUrl].lastPage = podcastData.last_page || 1;
    },
    setPodcastFeedDataError(state, action) {
      const podcastUrl = action.payload.podcastUrl;
      if (!state.podcastFeeds[podcastUrl]) {
        state.podcastFeeds[podcastUrl] = {
          data: null,
          state: DataState.NOT_LOADED,
          currentPage: 1,
          lastPage: 1,
          order: 'desc',
        };
      }
      state.podcastFeeds[podcastUrl].state = DataState.ERROR;
    },
    appendPodcastFeedData(state, action) {
      const { podcastUrl, podcastData } = action.payload;
      if (state.podcastFeeds[podcastUrl] && state.podcastFeeds[podcastUrl].data) {
        // append new episodes to existing data
        state.podcastFeeds[podcastUrl].data!.data = [
          ...state.podcastFeeds[podcastUrl].data!.data,
          ...podcastData.data,
        ];
        state.podcastFeeds[podcastUrl].currentPage = podcastData.current_page || 1;
        state.podcastFeeds[podcastUrl].lastPage = podcastData.last_page || 1;
        state.podcastFeeds[podcastUrl].state = DataState.LOADED;
      }
    },
    setPodcastFeedOrder(state, action) {
      const { podcastUrl, order } = action.payload;
      if (state.podcastFeeds[podcastUrl]) {
        state.podcastFeeds[podcastUrl].order = order;
      }
    },
  },
});

export const {
  setPodcastListAndEpisodesLoading,
  setPodcastListAndEpisodesLoaded,
  setPodcastListAndEpisodesError,
  setPodcastFeedDataLoading,
  setPodcastFeedDataLoaded,
  setPodcastFeedDataError,
  appendPodcastFeedData,
  setPodcastFeedOrder,
} = podcastSlice.actions;

export const podcastReducer = podcastSlice.reducer;
